/**
 * Navigation State
 *
 * Tracks:
 * - Current location (synced from React Router)
 * - Navigation history (audit trail)
 * - Pending navigation requests
 * - Navbar UI state (menus, drawer, account, QR modal)
 */

import { ROUTE_REGISTRY, RouteDefinition, canSourceNavigate } from '../routeRegistry';
import { AccountsReadmodelType } from '@bett3r-dev/teselly-domain';

/**
 * History entry for audit trail
 */
export interface NavigationHistoryEntry {
  path: string;
  params: Record<string, string>;
  timestamp: number;
  source: 'user' | 'ai' | 'url';
}

/**
 * Navigation state shape
 */
export interface NavigationState {
  /** Current path (synced from React Router) */
  currentPath: string;

  /** Current route params */
  currentParams: Record<string, string>;

  /** Navigation history (audit trail) */
  history: NavigationHistoryEntry[];

  /** Pending navigation request (triggers React Router) */
  pendingNavigation: {
    path: string;
    params?: Record<string, string>;
    source: 'user' | 'ai' | 'url';
  } | null;
}

/**
 * Initial state
 */
export const initialState: NavigationState = {
  currentPath: '/',
  currentParams: {},
  history: [],
  pendingNavigation: null
};

/**
 * Selectors for querying navigation state
 */
export const selectors = {
  /**
   * Get routes accessible to AI (static, from registry)
   */
  aiAccessibleRoutes: (): RouteDefinition[] => {
    return ROUTE_REGISTRY.filter(
      route => route.allowedSources.includes( 'ai' )
    );
  },

  /**
   * Get routes by category
   */
  routesByCategory: ( category: RouteDefinition['category']): RouteDefinition[] => {
    return ROUTE_REGISTRY.filter( route => route.category === category );
  },

  /**
   * Check if a path is accessible to a source
   */
  isPathAccessible: ( path: string, source: 'user' | 'ai' | 'url' ): boolean => {
    return canSourceNavigate( path, source );
  },

  /**
   * Get recent navigation history
   */
  recentHistory: ( state: NavigationState, limit: number = 10 ): NavigationHistoryEntry[] => {
    return state.history.slice( -limit );
  },

  /**
   * Get current route definition
   */
  currentRoute: ( state: NavigationState ): RouteDefinition | undefined => {
    // Handle parameterized routes by matching base path
    const currentBasePath = state.currentPath.split( '/' ).slice( 0, 2 )
.join( '/' );
    return ROUTE_REGISTRY.find( r =>
      r.path === state.currentPath ||
      r.path.startsWith( currentBasePath ));
  }
};
